import { useEffect } from "react";

/** The keycap face treatments; backlit is the fixed style (the others were design-session alternates). */
export type CapStyle = "backlit" | "tint" | "bar" | "dot" | "plain";

/**
 * The frozen visual choices, formerly the settings panel's knobs: backlit caps, ability
 * names shown, the chosen backlight strength and underglow intensity. These are decisions,
 * not options. Keycap art stays on names because the icon tiles are placeholders until
 * real ability art lands, so the names/icons toggle is retired here too. Applied to every
 * render surface and to the document styles below.
 *
 * Light mode was dropped for v1: the app is dark-only (Steel),
 * so there is no longer a user-controlled theme - every visual choice is fixed.
 */
export const FIXED_TWEAKS = {
  capStyle: "backlit" as CapStyle,
  capLabel: "names" as const,
  /** Backlight strength: the chosen 0.85 (was the slider default). */
  keyGlow: 0.85,
  /** Underglow on, at the chosen intensity 1 (was the slider default). */
  glowOn: true,
  glow: 1,
  showNames: true,
};

type UnderglowVars = { "--kb-glow": string; "--kb-bed": string };

/**
 * The underglow CSS variables for the plate halo - a continuous knob, so JS-set on
 * the root rather than a discrete palette. Intensity scales opacity, blur
 * and reach together. Steel (dark) is the only theme now: a warm ember rim over a dark
 * radial bed behind the plate.
 */
export function underglowVars(glowOn: boolean, glow: number): UnderglowVars {
  const g = glowOn ? Math.max(0, glow) : 0;
  if (g <= 0) return { "--kb-glow": "0 0 0 0 transparent", "--kb-bed": "transparent" };
  const n = (x: number) => x.toFixed(1);
  const a = (x: number) => x.toFixed(3);
  return {
    "--kb-glow": `0 0 ${n(36 * g)}px ${n(8 * g)}px rgba(239,94,42,${a(0.26 * g)}), 0 0 ${n(78 * g)}px ${n(26 * g)}px rgba(239,94,42,${a(0.14 * g)})`,
    "--kb-bed": `radial-gradient(ellipse 54% 58% at 50% 47%, rgba(239,94,42,${a(0.1 * Math.min(g, 1))}), transparent 70%)`,
  };
}

/**
 * Applies the fixed Steel (dark) document styling on mount: the dim+backlit
 * body classes that gate the plate rules in app.css, the backlight strength, and the
 * underglow CSS variables. The app is dark-only, so nothing here is user-adjustable or
 * persisted - this is a one-time document side effect, not a state hook.
 */
export function useTweaks() {
  useEffect(() => {
    const root = document.documentElement;
    // dim = the dark plate insets (always on now Cloud is gone); backlit = the charcoal-plate
    // cap style. Both gate plate rules in app.css; kept applied so the styling is unchanged.
    root.classList.add("is-dim");
    root.classList.toggle("is-backlit", FIXED_TWEAKS.capStyle === "backlit");
    root.style.setProperty("--key-glow", String(Math.max(0, FIXED_TWEAKS.keyGlow)));
    const vars = underglowVars(FIXED_TWEAKS.glowOn, FIXED_TWEAKS.glow);
    root.style.setProperty("--kb-glow", vars["--kb-glow"]);
    root.style.setProperty("--kb-bed", vars["--kb-bed"]);
  }, []);
}
